import {
    COMPONENT_JOB_SOURCE_EXTENSIONS,
    getComponentJobRootPath,
    getComponentJobSourcePath,
    parseComponentJobPath,
    type ComponentRunStatusRecord,
} from '@secureblob/sdk/componentJobs'
import type { FileStat } from '../../engine/types'

/**
 * Resolve the actual source file path for a component job, checking the real
 * file list instead of defaulting to `main.tsx`.
 *
 * The SDK's `getComponentJobSourcePath` is a pure path function that always
 * defaults to `main.tsx` for folder-first `.job` roots. When the actual source
 * file uses a different extension (`.ts`, `.js`, …), callers must resolve the
 * real path against the workspace file list.
 *
 * Returns the resolved source path, or `null` if the path is not a component
 * job path.
 */
export function resolveComponentJobSourcePath(
    path: string,
    files: ReadonlySet<string> | ReadonlyArray<FileStat>,
): string | null {
    const defaultSourcePath = getComponentJobSourcePath(path)
    if (!defaultSourcePath) return null

    const jobRoot = getComponentJobRootPath(defaultSourcePath)
    if (!jobRoot) {
        // Source-first (file-backed) job — the default IS the actual file.
        return defaultSourcePath
    }

    // If the default already exists, use it.
    const fileSet = files instanceof Set ? files : new Set((files as readonly FileStat[]).map(f => f.path))
    if (fileSet.has(defaultSourcePath)) return defaultSourcePath

    // Try other valid source extensions in SDK-defined order.
    for (const ext of COMPONENT_JOB_SOURCE_EXTENSIONS) {
        const candidate = `${jobRoot}/main${ext}`
        if (candidate !== defaultSourcePath && fileSet.has(candidate)) {
            return candidate
        }
    }

    // Fall back to SDK default even if it doesn't exist yet (new job scaffold).
    return defaultSourcePath
}

export interface JsonObject {
    [key: string]: unknown
}

export interface ComponentJobRunSummary {
    runId: string
    metadataPath: string
    startedAt: number
    startedAtLabel: string
    nextRunAt?: number
    nextRunAtLabel?: string
    status: string
    statusLabel: string
    target: string
    engine?: string
    summary?: string
    artifactId?: string
}

export interface ComponentJobBuildSummary {
    artifactId: string
    manifestPath: string
    createdAt: number
    createdAtLabel: string
    status: string
    statusLabel: string
    recommendedServerEngine?: string
}

export function safeParseJson(text: string | null): JsonObject | null {
    if (typeof text !== 'string' || text.trim().length === 0) return null
    try {
        const value = JSON.parse(text) as unknown
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as JsonObject
        }
    } catch {
        return null
    }
    return null
}

export function pickString(record: JsonObject | null, keys: readonly string[]): string | undefined {
    if (!record) return undefined
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim()
        }
    }
    return undefined
}

export function pickTimestamp(record: JsonObject | null, keys: readonly string[]): number | undefined {
    if (!record) return undefined
    for (const key of keys) {
        const value = record[key]
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            return value
        }
        if (typeof value === 'string' && value.trim().length > 0) {
            const epoch = Date.parse(value)
            if (Number.isFinite(epoch) && epoch > 0) {
                return epoch
            }
        }
    }
    return undefined
}

export function formatComponentJobStatusLabel(status: string): string {
    return status
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, match => match.toUpperCase())
}

export function formatComponentJobHistoryTimestamp(timestamp: number): string {
    const date = new Date(timestamp)
    return `${date.toLocaleDateString('sv')} ${date.toLocaleTimeString('sv', { hour: '2-digit', minute: '2-digit' })}`
}

export function shortComponentJobArtifactId(artifactId: string): string {
    return artifactId.length > 8 ? artifactId.slice(0, 8) : artifactId
}

export function isRunningComponentJobStatus(status: string): boolean {
    const normalized = status.trim().toLowerCase()
    return normalized === 'running' || normalized === 'canceling'
}

export function isQueuedComponentJobStatus(status: string): boolean {
    const normalized = status.trim().toLowerCase()
    return normalized === 'queued'
        || normalized === 'building'
        || normalized === 'pending'
        || normalized === 'starting'
        || normalized === 'materializing'
}

export function isActiveComponentJobStatus(status: string): boolean {
    return isRunningComponentJobStatus(status) || isQueuedComponentJobStatus(status)
}

export function getComponentJobSourcePaths(files: readonly FileStat[]): string[] {
    const sourcePaths = new Set<string>()
    const seenRoots = new Set<string>()
    const fileSet = new Set(files.map(f => f.path))

    for (const file of files) {
        const sourcePath = getComponentJobSourcePath(file.path)
        if (!sourcePath) continue

        const jobRoot = getComponentJobRootPath(sourcePath)
        if (jobRoot) {
            // Folder-first job: only one entrypoint per root.
            // If both main.ts and main.tsx exist, resolve to the actual file.
            if (seenRoots.has(jobRoot)) continue
            seenRoots.add(jobRoot)
            const resolved = resolveComponentJobSourcePath(jobRoot, fileSet)
            if (resolved) sourcePaths.add(resolved)
        } else {
            // Source-first (file-backed) job: add directly.
            sourcePaths.add(sourcePath)
        }
    }
    return [...sourcePaths].sort((left, right) => left.localeCompare(right))
}

export async function loadComponentJobRunSummary(
    file: FileStat,
    readFile: (path: string) => Promise<string | null>,
): Promise<ComponentJobRunSummary | null> {
    const parsed = parseComponentJobPath(file.path)
    if (!parsed?.runId) return null

    const record = safeParseJson(await readFile(file.path))
    const startedAt = pickTimestamp(record, ['startedAt', 'startedAtMs', 'createdAt', 'timestamp']) ?? file.mtime
    const nextRunAt = pickTimestamp(record, ['nextRunAt', 'scheduledFor', 'scheduledAt'])
    const status = pickString(record, ['status', 'state', 'result', 'outcome']) ?? 'completed'

    return {
        runId: parsed.runId,
        metadataPath: file.path,
        startedAt,
        startedAtLabel: formatComponentJobHistoryTimestamp(startedAt),
        nextRunAt,
        nextRunAtLabel: nextRunAt ? formatComponentJobHistoryTimestamp(nextRunAt) : undefined,
        status,
        statusLabel: formatComponentJobStatusLabel(status),
        target: pickString(record, ['target', 'runtimeTarget', 'targetKind']) ?? 'server',
        engine: pickString(record, ['engine', 'engineId', 'recommendedServerEngine']),
        summary: pickString(record, ['summary', 'resultSummary', 'message']),
        artifactId: pickString(record, ['artifactId']),
    }
}

export function createComponentJobRunSummaryFromStatusRecord(
    status: ComponentRunStatusRecord,
    metadataPath: string,
    target = 'server',
): ComponentJobRunSummary {
    const startedAt = parseHistoryTimestamp(status.startedAt, Date.now())

    return {
        runId: status.runId,
        metadataPath,
        startedAt,
        startedAtLabel: formatComponentJobHistoryTimestamp(startedAt),
        status: status.stage,
        statusLabel: formatComponentJobStatusLabel(status.stage),
        target,
        engine: status.engine,
        summary: status.message,
        artifactId: status.artifactId,
    }
}

export async function loadComponentJobBuildSummary(
    file: FileStat,
    readFile: (path: string) => Promise<string | null>,
): Promise<ComponentJobBuildSummary | null> {
    const parsed = parseComponentJobPath(file.path)
    if (!parsed?.artifactId) return null

    const record = safeParseJson(await readFile(file.path))
    const createdAt = pickTimestamp(record, ['createdAt', 'builtAt', 'timestamp']) ?? file.mtime
    const status = pickString(record, ['status']) ?? 'ready'

    return {
        artifactId: parsed.artifactId,
        manifestPath: file.path,
        createdAt,
        createdAtLabel: formatComponentJobHistoryTimestamp(createdAt),
        status,
        statusLabel: formatComponentJobStatusLabel(status),
        recommendedServerEngine: pickString(record, ['recommendedServerEngine', 'engine']),
    }
}

function parseHistoryTimestamp(value: string, fallback: number): number {
    const timestamp = Date.parse(value)
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : fallback
}
